import parser from "cron-parser";
import { validateCron } from "@prodgy/schedule";

/**
 * O que o horário de um Job precisa só na tela (AB#87330). Presets, validação e
 * descrição vêm de `@prodgy/schedule`, as mesmas regras que a api aplica.
 */

export const DEFAULT_TIMEZONE = "America/Sao_Paulo";
export const DEFAULT_TIME = "09:00";
export const PREVIEW_RUNS = 3;

/**
 * O fuso de quem está criando o job. Usuário e workspace não têm fuso
 * cadastrado; o do navegador é o melhor palpite, e ele fica editável.
 */
export function defaultTimezone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && isTimezone(zone) ? zone : DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/** Os fusos IANA que o navegador conhece, com o atual garantido na lista. */
export function timezoneOptions(current?: string): string[] {
  // `supportedValuesOf` é ES2022; o tsconfig da web tipa até ES2020.
  const intl = Intl as unknown as { supportedValuesOf?: (key: "timeZone") => string[] };
  const zones = intl.supportedValuesOf?.("timeZone") ?? [DEFAULT_TIMEZONE];
  const all = new Set(zones);
  // "UTC" não aparece na lista de alguns navegadores, e é o que muita gente usa.
  all.add("UTC");
  if (current) all.add(current);
  return [...all].sort((a, b) => a.localeCompare(b));
}

export function isTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * As próximas execuções no fuso do job. `cron-parser` lê o cron como o engine
 * (0 e 7 = domingo, `L`, dia do mês OU dia da semana); vazio quando o cron ou o
 * fuso não valem.
 */
export function nextRuns(cron: string, timezone: string, count = PREVIEW_RUNS, from = new Date()): Date[] {
  if (!validateCron(cron).ok || !isTimezone(timezone)) return [];
  try {
    const iterator = parser.parseExpression(cron, { currentDate: from, tz: timezone });
    return Array.from({ length: count }, () => iterator.next().toDate());
  } catch {
    return [];
  }
}

/** Uma execução da prévia, no fuso do job e no idioma de quem lê. */
export function formatRun(date: Date, timezone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}
